import React, { useState, useEffect } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import toast, { Toaster } from "react-hot-toast";
import axios from "../Util/axiosConfig";
import PureModal from "react-pure-modal";

const Resources = ({ authUser }) => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadModal, setUploadModal] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    title: "",
    description: "",
    type: "book",
    isPublic: true,
    file: null,
  });

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const endpoint = authUser?.admin
        ? "/v1/api/resources/all"
        : "/v1/api/resources";
      const response = await axios.get(endpoint);

      if (response.data.success) {
        setResources(response.data.data);
      }
    } catch (error) {
      toast.error("Failed to fetch resources");
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      Object.keys(uploadForm).forEach((key) => {
        formData.append(key, uploadForm[key]);
      });

      const response = await axios.post("/v1/api/resources/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        toast.success("Resource uploaded successfully");
        setUploadModal(false);
        setUploadForm({
          title: "",
          description: "",
          type: "book",
          isPublic: true,
          file: null,
        });
        fetchResources();
      }
    } catch (error) {
      toast.error("Failed to upload resource");
    }
  };

  const handleDownload = async (id) => {
    try {
      const response = await axios.get(`/v1/api/resources/download/${id}`);
      if (response.data.success) {
        const { downloadUrl, contentType, fileName } = response.data.data;

        // Create an anchor element
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.setAttribute("download", fileName);
        link.setAttribute("type", contentType);

        // Append to body, click, and remove
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      toast.error("Failed to generate download link");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this resource?")) {
      try {
        await axios.delete(`/v1/api/resources/${id}`);
        toast.success("Resource deleted successfully");
        fetchResources();
      } catch (error) {
        toast.error("Failed to delete resource");
      }
    }
  };

  const handleGetLink = async (id) => {
    try {
      const response = await axios.post(
        `/v1/api/resources/generate-link/${id}`
      );
      if (response.data.success) {
        const { downloadLink, expiry } = response.data.data;

        // Create a temporary input to copy the link
        const input = document.createElement("input");
        input.value = downloadLink;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);

        toast.success(
          `Download link copied to clipboard! Link expires on ${new Date(
            expiry
          ).toLocaleString()}`
        );
      }
    } catch (error) {
      toast.error("Failed to generate download link");
    }
  };

  return (
    <React.Fragment>
      <HelmetProvider>
        <Helmet>
          <title>Resources - UGONSA</title>
        </Helmet>
        <Toaster />

        <div className="container-fluid mt-3">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="card-title">Resources</h4>
                    {authUser?.admin && (
                      <button
                        className="btn btn-primary"
                        onClick={() => setUploadModal(true)}
                      >
                        Upload New Resource
                      </button>
                    )}
                  </div>

                  {loading ? (
                    <p>Loading...</p>
                  ) : (
                    <div className="table-responsive">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Title</th>
                            <th>Type</th>
                            <th>Description</th>
                            <th>Downloads</th>
                            <th>Status</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {resources.map((resource) => (
                            <tr key={resource.id}>
                              <td>{resource.title}</td>
                              <td>{resource.type}</td>
                              <td>{resource.description}</td>
                              <td>{resource.downloadCount}</td>
                              <td>
                                <span
                                  className={`badge bg-${
                                    resource.isPublic ? "success" : "warning"
                                  }`}
                                >
                                  {resource.isPublic ? "Public" : "Private"}
                                </span>
                              </td>
                              <td>
                                <button
                                  className="btn btn-sm btn-primary me-2"
                                  onClick={() => handleDownload(resource.id)}
                                >
                                  Download
                                </button>
                                <button
                                  className="btn btn-sm btn-info me-2"
                                  onClick={() => handleGetLink(resource.id)}
                                >
                                  Get Link
                                </button>
                                {authUser?.admin && (
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => handleDelete(resource.id)}
                                  >
                                    Delete
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <PureModal
          header="Upload Resource"
          isOpen={uploadModal}
          closeButton="X"
          closeButtonPosition="header"
          onClose={() => {
            setUploadModal(false);
            return true;
          }}
        >
          <form onSubmit={handleUpload} className="p-3">
            <div className="form-group mb-3">
              <label>Title</label>
              <input
                type="text"
                className="form-control"
                value={uploadForm.title}
                onChange={(e) =>
                  setUploadForm({ ...uploadForm, title: e.target.value })
                }
                required
              />
            </div>
            <div className="form-group mb-3">
              <label>Description</label>
              <textarea
                className="form-control"
                rows="3"
                value={uploadForm.description}
                onChange={(e) =>
                  setUploadForm({ ...uploadForm, description: e.target.value })
                }
              />
            </div>
            <div className="form-group mb-3">
              <label>Type</label>
              <select
                className="form-control"
                value={uploadForm.type}
                onChange={(e) =>
                  setUploadForm({ ...uploadForm, type: e.target.value })
                }
              >
                <option value="book">Book</option>
                <option value="software">Software</option>
              </select>
            </div>
            <div className="form-group mb-3">
              <label>File</label>
              <input
                type="file"
                className="form-control"
                onChange={(e) =>
                  setUploadForm({ ...uploadForm, file: e.target.files[0] })
                }
                required
              />
            </div>
            <div className="form-check mb-3">
              <input
                type="checkbox"
                className="form-check-input"
                checked={uploadForm.isPublic}
                onChange={(e) =>
                  setUploadForm({ ...uploadForm, isPublic: e.target.checked })
                }
              />
              <label className="form-check-label">Make Public</label>
            </div>
            <button type="submit" className="btn btn-primary">
              Upload
            </button>
          </form>
        </PureModal>
      </HelmetProvider>
    </React.Fragment>
  );
};

export default Resources;
